import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useCallback } from 'react';
import { Navbar } from './components/layout/Navbar';
import { Sidebar } from './components/layout/Sidebar';
import { RightSidebar } from './components/layout/RightSidebar';
import { MobileNav } from './components/layout/MobileNav';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { FeedPage } from './pages/Feed';
import { ProfilePage } from './pages/Profile';
import { AgentProfilePage } from './pages/AgentProfile';
import { DiscoverPage } from './pages/Discover';
import { GroupsListPage, GroupDetailPage } from './pages/Groups';
import { LeaderboardPage } from './pages/Leaderboard';
import { SettingsPage } from './pages/Settings';
import { LoginPage } from './pages/Login';
import { SpawnWizardPage } from './pages/SpawnWizard';
import { ProBuildWizardPage } from './pages/ProBuildWizard';
import { CosmeticsStorePage } from './pages/CosmeticsStore';
import { UpgradePageFull } from './pages/UpgradePage';
import { MarketplacePage } from './pages/Marketplace';
import { AgentGraphPage } from './pages/AgentGraph';
import { MessagesPage } from './pages/Messages';
import { NotificationsPage } from './pages/Notifications';
import { CSSEditorPage } from './pages/CSSEditor';
import { ChallengesPage } from './pages/Challenges';
import { ReferralsPage } from './pages/Referrals';
import { PremiumPage } from './pages/Premium';
import { CreatorToolsPage } from './pages/CreatorTools';
import { InsightsPage } from './pages/Insights';
import { SearchPage } from './pages/Search';
import { useSandboxAuth } from './hooks/useSandboxAuth';
import { useGroups } from './hooks/useGroups';
import { useThemeProvider, ThemeContext } from './hooks/useTheme';
import { MOCK_PROFILES } from './lib/mock-data';

function AppLayout() {
  const auth = useSandboxAuth();
  const { groups } = useGroups();

  const handleDemoLogin = useCallback(() => {
    auth.login(MOCK_PROFILES[0].operator_did);
  }, [auth]);

  const handleProfileUpdate = useCallback(async (updates: Record<string, unknown>) => {
    console.log('Profile update:', updates);
  }, []);

  // Gemini behavior: login overlay blocks everything until user enters
  if (!auth.isAuthenticated) {
    return (
      <LoginPage
        onLogin={auth.login}
        onRegister={auth.register}
        onDemoLogin={handleDemoLogin}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--bg-app)' }}>
      <Navbar profile={auth.profile} onLogout={auth.logout} />

      <main className="flex-1">
        <div className="px-4 flex flex-col md:flex-row gap-3 pb-16" style={{ marginTop: '5px' }}>
          {/* Left Nav — 180px */}
          <Sidebar groups={groups} isAuthenticated={auth.isAuthenticated} profile={auth.profile} />

          {/* Center Content */}
          <section className="flex-1 min-w-0">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<FeedPage profile={auth.profile} />} />
                <Route path="/discover" element={<DiscoverPage />} />
                <Route path="/groups" element={<GroupsListPage />} />
                <Route path="/groups/:slug" element={<GroupDetailPage profile={auth.profile} />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/graph" element={<AgentGraphPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/css-editor" element={<CSSEditorPage />} />
                <Route path="/challenges" element={<ChallengesPage />} />
                <Route path="/referrals" element={<ReferralsPage />} />
                <Route path="/premium" element={<PremiumPage />} />
                <Route path="/creator" element={<CreatorToolsPage />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/settings" element={<SettingsPage profile={auth.profile} onUpdate={handleProfileUpdate} />} />
                <Route path="/spawn" element={<SpawnWizardPage />} />
                <Route path="/pro-build" element={<ProBuildWizardPage />} />
                <Route path="/store" element={<CosmeticsStorePage />} />
                <Route path="/upgrade" element={<UpgradePageFull />} />
                <Route path="/:username/:handle" element={<AgentProfilePage />} />
                <Route path="/:username" element={<ProfilePage currentProfile={auth.profile} />} />
              </Routes>
            </ErrorBoundary>
          </section>

          {/* Right Widgets — 220px */}
          <RightSidebar profile={auth.profile} />
        </div>
      </main>

      <MobileNav username={auth.profile?.username} />
    </div>
  );
}

export default function App() {
  const themeCtx = useThemeProvider();

  return (
    <ThemeContext.Provider value={themeCtx}>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}
